import { makeAutoObservable } from "mobx"
import { ICategory, ICompetition, Status } from "../utils/interfaces"
import { status } from "../utils/constants"
import uiStore from "./uiStore"
import { fetchCompetitionRequest, toggleCompetitionFavouriteRequest } from "../utils/apiRequests"
import seasonStore from "./seasonStore"
import { isNumberGreaterThanX, retrieveErrorMessage } from "../utils/helpers"
import contestantStore from "./contestantStore"
import votingRoundStore from "./votingRoundStore"
import cloneDeep from "lodash/cloneDeep"
import isEmpty from "lodash/isEmpty"
import creditStore from "./creditStore"
import competitionStore from "./competitionStore"

type TStatus = {
  fetchInfo?: Status
  toggleFavourite?: Status
}

type TErrorMessage = {
  vote: string
  fetchInfo: string
  toggleFavourite: string
}

export type TabType = 'Contestants' | 'Results' | 'Discussions' | 'Voting_options'

type TCompetitionPageData = {
  competitionInfo: ICompetition
  status: TStatus
  errorMessages: TErrorMessage
  selectedCategory?: ICategory
  voteCount: number
  showVoteCount: boolean
  contestantUsername: string
  currentTab: TabType
  tabs: TabType[]
}

export const CONTESTANT_TAB = 'Contestants'
export const RESULT_TAB = 'Results'
export const DISCUSSION_TAB = 'Discussions'
export const VOTING_OPTIONS_TAB = 'Voting_options'

export const routeNames: Record<TabType, string> = {
  [CONTESTANT_TAB]: 'contestants',
  [RESULT_TAB]: 'results',
  [DISCUSSION_TAB]: 'discussions',
  [VOTING_OPTIONS_TAB]: 'voting_options'
}

const defaultCompetitionPageData: TCompetitionPageData = {
  competitionInfo: {} as ICompetition,
  status: {
    fetchInfo: undefined,
    toggleFavourite: undefined
  },
  errorMessages: {
    fetchInfo: '',
    vote: '',
    toggleFavourite: ''
  },
  selectedCategory: undefined,
  voteCount: 1,
  showVoteCount: false,
  contestantUsername: '',
  tabs: [CONTESTANT_TAB],
  currentTab: CONTESTANT_TAB
}

class CompetitionPageStore {
  data = cloneDeep(defaultCompetitionPageData)

  constructor() {
    makeAutoObservable(this)
  }

  gettingInfo = () => {
    this.data.status.fetchInfo = status.LOADING
    this.data.errorMessages.fetchInfo = ''
    uiStore.setLoadingScreenMessage('Fetching competition information')
  }

  fetchFailed = (e: any) => {
    this.data.status.fetchInfo = status.FAILED
    this.data.errorMessages.fetchInfo = retrieveErrorMessage(e)
  }

  fetchComplete = (payload: ICompetition) => {
    this.data.status.fetchInfo = status.COMPLETE
    uiStore.setLoadingScreenMessage('')

    seasonStore.setSeasons(payload.seasons)
    seasonStore.setSelectedSeason(seasonStore.latestSeason)

    this.data.competitionInfo = payload
    this.data.selectedCategory = payload.categories?.find((e: ICategory) => e.is_default)
  }

  clearCompetitionPageData = () => {
    this.data = cloneDeep(defaultCompetitionPageData)
  }

  closeModal = () => {
    this.data.voteCount = 1
    this.data.showVoteCount = false
  }

  setVoteError = (error: string) => {
    this.data.errorMessages.vote = error
  }

  cannotVote = () => {
    let error = null
    const balance = creditStore.creditBalance
    const costTotal = (this.votingOptions?.vote_cost || 0) * this.voteCount
    
    if (costTotal > balance) {
      error = 'You don\'t have enough balance'
    } else if (!this.data.selectedCategory?.id) {
      error = 'Please select a category'
    }

    if (error) {
      uiStore.errorModal(error) 
      this.setVoteError(error)
    }
    return !!error
  }

  setCurrentTab = (tab: TabType) => {
    this.data.currentTab = tab
  }

  refreshContestantVotes = () => {
    if (!seasonStore.selectedSeasonId) return
    votingRoundStore.fetchVotingRounds(seasonStore.selectedSeasonId)
  }

  onVoteCountChange = (voteCount: number) => {
    if (!isNumberGreaterThanX(0)(voteCount)) return
    this.clearErrorMessages()
    this.data.voteCount = voteCount
  }

  onSubmitForm = () => {
    if (this.cannotVote()) return
    if (!this.data.selectedCategory?.id) return

    contestantStore.voteContestant({
      contestantUsername: this.contestantUsername, 
      voteCount: this.voteCount, 
      seasonId: seasonStore.selectedSeasonId,
      categoryId: this.data.selectedCategory?.id
    })
    this.closeModal()
  }

  onVote = (contestantUsername: string) => () => {
    if (this.cannotVote()) return
    if (!this.data.selectedCategory?.id) return

    if (this.votingOptions?.max_no_of_votes === 1) {
      uiStore.openConfirmDialog('Are you sure you want to vote for this contestant?', () => {
        contestantStore.voteContestant({
          contestantUsername, 
          voteCount: this.voteCount, 
          seasonId: seasonStore.selectedSeasonId,
          categoryId: this.data.selectedCategory?.id
        })
        this.data.showVoteCount = false
      })
      return
    }

    this.data.contestantUsername = contestantUsername
    this.data.showVoteCount = true
  }

  clearErrorMessages = () => {
    this.data.errorMessages = {
      fetchInfo: '',
      vote: '',
      toggleFavourite: ''
    }
  }

  setSelectedCategory = (category: ICategory) => {
    this.data.selectedCategory = category
  }

  togglingFavouriteCompetition = () => {
    this.data.status.toggleFavourite = status.LOADING
    this.data.errorMessages.toggleFavourite = ''
  }

  toggleFavouriteComplete = () => {
    this.data.status.toggleFavourite = status.COMPLETE
    this.data.errorMessages.toggleFavourite = ''
  }

  toggleFavouriteFailed = () => {
    this.data.status.toggleFavourite = status.FAILED
    this.data.errorMessages.toggleFavourite = ''
  }

  async loadPageData(competitionUsername: string) {
    await this.getCompetitionInfo(competitionUsername)
    if (this.fetchingInfoFailed) return
    Promise.all([
      contestantStore.fetchContestants(competitionUsername, seasonStore.selectedSeasonId),
      votingRoundStore.fetchVotingRounds(seasonStore.selectedSeasonId)
    ])
  }

  async getCompetitionInfo(competitionUsername: string) {
    if (this.fetchingInfo) return
    this.gettingInfo()

    try {
      const response = await fetchCompetitionRequest(competitionUsername)
      this.fetchComplete(response.data)
    } catch (e) {
      this.fetchFailed(e)
    }

    uiStore.setLoadingScreenMessage('')
  }

  async toggleFavouriteCompetition() {
    const competition = this.data.competitionInfo
    if (this.toggleFavouritesLoading) return
    this.togglingFavouriteCompetition()

    try {
      competitionStore.updateFavouritesData(competition)
      await toggleCompetitionFavouriteRequest(competition.username)
      this.toggleFavouriteComplete()
    } catch (e) {
      competitionStore.updateFavouritesData(competition)
      this.toggleFavouriteFailed()
    } 
  }

  get toggleFavouritesLoading() {
    return this.data.status.toggleFavourite === status.LOADING
  }

  get competitionCategories() {
    return this.data.competitionInfo.categories || []
  }

  get fetchingInfo() {
    return this.data.status.fetchInfo === status.LOADING
  }

  get fetchingInfoFailed() {
    return this.data.status.fetchInfo === status.FAILED
  }

  get noCompetition() {
    return isEmpty(this.data.competitionInfo)
  }

  get voteCount() {
    return this.data.voteCount
  }

  get votingOptions() {
    return votingRoundStore.selectedVotingRound?.voting_option
  }

  get showVoteCount() {
    return this.data.showVoteCount
  }

  get contestantUsername() {
    return this.data.contestantUsername
  }

  get competitionUsername() {
    return this.data.competitionInfo.username
  }

  get tabs() {
    if (votingRoundStore.hasVotingRounds) {
      return [...this.data.tabs, RESULT_TAB, VOTING_OPTIONS_TAB] as TabType[]
    }
    return this.data.tabs
  }

  get currentTab() {
    return this.data.currentTab
  }

  get isFavourite() {
    return !!competitionStore.favouriteCompetitions.find(c => c.id === this.data.competitionInfo.id)
  }
}

const competitionPageStore = new CompetitionPageStore()
export default competitionPageStore